import { BaseEffect } from '../BaseEffect.js';

/**
 * Glitch effect implementation
 */
export class GlitchEffect extends BaseEffect {
    constructor(shaderManager) {
        const defaultParams = {
            rgbShift: 0.0137,
            rgbOffsetX: 1.0,
            rgbOffsetY: 0.0,
            glitchIntensity: 0.49,
            scanlineIntensity: 0.34,
            animate: true
        };

        super('glitch', shaderManager, defaultParams);
        
        // Initialize audio state with default non-zero values to ensure effect works without audio
        this.audioData = {
            volume: 0.3,  // Default volume to ensure some effect
            frequency: {
                low: 0.2,   // Default bass level
                mid: 0.2,   // Default mid level
                high: 0.2   // Default high level
            }
        };

        // Debug flag
        this.lastDebugTime = 0;
        
        // Add time offset for mobile animation smoothing
        this.timeOffset = Math.random() * 1000;
    }

    /**
     * Initialize the effect
     */
    async init() {
        await super.init();  // This will load glitch.vert and glitch.frag from /public/shaders/
    }

    /**
     * Set up effect-specific uniforms
     */
    setupUniforms() {
        super.setupUniforms();
        
        this.addUniform('u_rgbShift', { type: '1f' });
        this.addUniform('u_rgbOffsetX', { type: '1f' });
        this.addUniform('u_rgbOffsetY', { type: '1f' });
        this.addUniform('u_glitchIntensity', { type: '1f' });
        this.addUniform('u_scanlineIntensity', { type: '1f' });
    }

    /**
     * Update effect-specific uniforms
     * @param {Object} uniforms - Uniform values to update
     */
    updateUniforms(uniforms) {
        // Add time offset for smoother animation on mobile
        const time = (uniforms.u_time.value + this.timeOffset) * 0.5;
        
        // Base values with time-based modulation
        const timeMod = (Math.sin(time * 2.0) + 1.0) * 0.5;
        
        // Base RGB shift with time modulation
        const baseRgbShift = this.params.rgbShift;
        const bassImpact = Math.pow(this.audioData.frequency.low, 2);
        const timeRgbShift = baseRgbShift * (1.0 + timeMod * 0.5);
        const audioRgbShift = timeRgbShift * (1 + bassImpact * 3);

        // Glitch intensity with time-based randomization
        const baseGlitchIntensity = this.params.glitchIntensity;
        const volumeImpact = Math.pow(this.audioData.volume, 1.5);
        const highFreqImpact = Math.pow(this.audioData.frequency.high, 2);
        const timeGlitch = baseGlitchIntensity * (1.0 + Math.sin(time * 3.0) * 0.3);
        const audioGlitchIntensity = timeGlitch * (1 + volumeImpact + highFreqImpact);

        // RGB offset with smoother time-based movement
        const midImpact = this.audioData.frequency.mid;
        const rgbOffsetX = this.params.rgbOffsetX * (1 + Math.sin(time) * 0.2 + midImpact);
        const rgbOffsetY = this.params.rgbOffsetY + Math.cos(time * 0.7) * 0.1 + (midImpact - 0.5) * 0.3;

        // Scanline intensity with time variation
        const baseScanlineIntensity = this.params.scanlineIntensity;
        const scanlineImpact = Math.pow(this.audioData.frequency.high, 1.5);
        const timeScanline = baseScanlineIntensity * (1.0 + Math.sin(time * 1.5) * 0.2);
        const audioScanlineIntensity = timeScanline * (1 + scanlineImpact);

        Object.assign(uniforms, {
            u_rgbShift: { value: audioRgbShift, type: '1f' },
            u_rgbOffsetX: { value: rgbOffsetX, type: '1f' },
            u_rgbOffsetY: { value: rgbOffsetY, type: '1f' },
            u_glitchIntensity: { value: audioGlitchIntensity, type: '1f' },
            u_scanlineIntensity: { value: audioScanlineIntensity, type: '1f' }
        });

        // If animation is disabled, use default values
        if (!this.params.animate) {
            Object.assign(uniforms, {
                u_time: { value: 0, type: '1f' },
                u_rgbShift: { value: this.params.rgbShift, type: '1f' },
                u_rgbOffsetX: { value: this.params.rgbOffsetX, type: '1f' },
                u_rgbOffsetY: { value: this.params.rgbOffsetY, type: '1f' },
                u_glitchIntensity: { value: this.params.glitchIntensity, type: '1f' },
                u_scanlineIntensity: { value: this.params.scanlineIntensity, type: '1f' }
            });
        }
    }

    /**
     * Handle incoming audio data
     * @param {Object} audioData - Audio analysis data
     */
    handleAudioData(audioData) {
        // Verify and store audio data with fallback to defaults
        if (audioData && typeof audioData.volume === 'number' && audioData.frequency) {
            this.audioData = audioData;
        }

        // Force a re-render by toggling animation briefly if not already animating
        if (!this.params.animate) {
            this.params.animate = true;
            requestAnimationFrame(() => {
                if (!this.params.animate) return;
                this.params.animate = false;
            });
        }
    }

    /**
     * Get UI configuration for parameters
     * @returns {Object} UI configuration
     */
    getUIConfig() {
        return {
            rgbShift: {
                min: 0,
                max: 0.02,
                step: 0.0001,
                label: 'RGB Shift'
            },
            rgbOffsetX: {
                min: -1,
                max: 1,
                step: 0.1,
                label: 'RGB X Offset'
            },
            rgbOffsetY: {
                min: -1,
                max: 1,
                step: 0.1,
                label: 'RGB Y Offset'
            },
            glitchIntensity: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Glitch Intensity'
            },
            scanlineIntensity: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Scanline Intensity'
            },
            animate: {
                label: 'Animate Effects'
            }
        };
    }
} 